import { createContext, useContext, useMemo, useState } from "react";
import * as geojson from "geojson";
import { ImageryOption } from "../types/dataReview";

export type DrawPurpose = "area" | "add" | "subtract";

/** What the vegetation editor is looking at: the shape in hand, a lasso
 *  selection, the drawing in progress, what shows on the map, and the photo
 *  and area being set up. Shared by the editor's panels and the
 *  map layers on the project page. */
interface DataReviewState {
  currentId: number | null;
  setCurrentId: (id: number | null) => void;
  /** What a polygon drawn on this screen is for: the area a run looks at,
   *  or ground added to or cut from the current outline. */
  drawPurpose: DrawPurpose;
  setDrawPurpose: (p: DrawPurpose) => void;
  /** Shapes picked together (lasso or shift-click) for one decision. */
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  /** Fine detail: the aerial photo the tool looked at, and the changes it
   *  proposes, each switchable so one can be judged against the other. */
  showImagery: boolean;
  setShowImagery: (v: boolean) => void;
  showChanges: boolean;
  setShowChanges: (v: boolean) => void;
  /** The photo source on the map, and the one the next run reads. */
  pickedImagery: ImageryOption | null;
  setPickedImagery: (o: ImageryOption | null) => void;
  /** The area just traced for fine detail, until its run starts: the map
   *  dims everything else so the picked photo is judged inside it. */
  tracedArea: geojson.Feature | null;
  setTracedArea: (f: geojson.Feature | null) => void;
  /** Cut-offs moved in this session, keyed "jobId:pass", so the map and the
   *  card hide the same findings the moment the slider moves. */
  passThresholds: Record<string, number>;
  setPassThreshold: (key: string, value: number) => void;
}

const DataReviewContext = createContext<DataReviewState>({
  currentId: null, setCurrentId: () => {},
  drawPurpose: "area", setDrawPurpose: () => {}, selectedIds: [], setSelectedIds: () => {},
  showImagery: true, setShowImagery: () => {}, showChanges: true, setShowChanges: () => {},
  pickedImagery: null, setPickedImagery: () => {}, tracedArea: null, setTracedArea: () => {},
  passThresholds: {}, setPassThreshold: () => {},
});

export const useDataReview = () => useContext(DataReviewContext);

export const DataReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [drawPurpose, setDrawPurpose] = useState<DrawPurpose>("area");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showImagery, setShowImagery] = useState(true);
  const [showChanges, setShowChanges] = useState(true);
  const [pickedImagery, setPickedImagery] = useState<ImageryOption | null>(null);
  const [tracedArea, setTracedArea] = useState<geojson.Feature | null>(null);
  const [passThresholds, setPassThresholds] = useState<Record<string, number>>({});
  const setPassThreshold = (key: string, v: number) => setPassThresholds((prev) => ({ ...prev, [key]: v }));
  const value = useMemo(
    () => ({
      currentId, setCurrentId, drawPurpose, setDrawPurpose, selectedIds, setSelectedIds,
      showImagery, setShowImagery, showChanges, setShowChanges,
      pickedImagery, setPickedImagery, tracedArea, setTracedArea, passThresholds, setPassThreshold,
    }),
    [currentId, drawPurpose, selectedIds, showImagery, showChanges, pickedImagery, tracedArea, passThresholds],
  );
  return <DataReviewContext.Provider value={value}>{children}</DataReviewContext.Provider>;
};
